import type { Dispatch } from 'redux'

import type { CmsPageFields } from '../page/types'
import {
  CMS_BLOCK_FETCH_START,
  CMS_BLOCK_FETCH_SUCCESS,
  CMS_BLOCK_FETCH_ERROR,
  cmsBlockKey,
} from './types'
import type { CmsBlockAction, CmsBlockDescriptor } from './types'

type FetcherSuccess = { ok: true; data: Record<string, unknown> }
type FetcherFailure = { ok: false; code: string; retryable: boolean; message: string }
export type CmsFetcher = (
  contentType: string,
  pageUrl: string,
) => Promise<FetcherSuccess | FetcherFailure>

export function fetchCmsBlocks(descriptors: CmsBlockDescriptor[], fetcher: CmsFetcher) {
  return async (dispatch: Dispatch<CmsBlockAction>): Promise<void> => {
    await Promise.all(
      descriptors.map(async (descriptor) => {
        const key = cmsBlockKey(descriptor)
        dispatch({ type: CMS_BLOCK_FETCH_START, key })

        try {
          const result = await fetcher(descriptor.contentType, descriptor.pageUrl)
          if (result.ok) {
            // Trust boundary: the fetcher returns Record<string, unknown> because it can't
            // know which page shape this descriptor maps to. The runtime guarantees of the
            // CmsPageFields shape come from ContentStack's schema enforcement at author time
            // and from `cms:types` keeping the TS types in sync with `schema.json`. Per-block
            // render errors (e.g. an unexpected null inside a section) are contained by
            // SafeBlock so one bad block can never take down the whole page.
            dispatch({
              type: CMS_BLOCK_FETCH_SUCCESS,
              key,
              data: result.data as unknown as CmsPageFields,
            })
          } else {
            dispatch({ type: CMS_BLOCK_FETCH_ERROR, key, errorCode: result.code })
          }
        } catch {
          dispatch({ type: CMS_BLOCK_FETCH_ERROR, key, errorCode: 'UNKNOWN' })
        }
      }),
    )
  }
}
